import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { tournamentDayApi, tournamentScheduleApi } from "@bw/shared/api";
import { connectBrowserTournamentLive } from "@bw/shared/hooks";
import type {
  TournamentExecutionCommand,
  TournamentPresenceBatch,
} from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { OperatorMatchInspector } from "./operator-match-inspector";
import { OperatorOperationsBoard } from "./operator-operations-board";
import { OperatorOrderOfPlay } from "./operator-order-of-play";
import { GameSheetPackageDialog } from "./game-sheet-package-dialog";
import { ScoreSheetResolverDialog } from "./score-sheet-resolver-dialog";
import { TournamentDispatchPanel } from "./tournament-dispatch-panel";
import { UmpireScorePanel } from "./umpire-score-panel";

type MatchRef = Schema["TournamentMatchRef"];
type MatchRow = Schema["TournamentOperatorMatch"];
type Snapshot = Schema["TournamentOperatorLiveSnapshot"];
type Presence = Schema["TournamentOperatorPresence"];
type Recovery = Schema["TournamentOperatorRecoverySnapshot"];
type OperatorCommand = TournamentExecutionCommand & { match: MatchRef };

const label = (value: string) => value.replaceAll("_", " ");
const selectedMatchKindKey = "match_kind";
const selectedMatchIdKey = "match_id";
const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const recoveryActionLabel = {
  review_blocked_matches: "Review blocked matches",
  review_called_matches: "Review called matches",
  review_schedule_constraints: "Review schedule constraints",
  review_dispatcher: "Review dispatcher",
} as const;

type SelectedMatchSearch = {
  invalid: boolean;
  match: MatchRef | null;
};

function selectedMatchFromSearch(search: URLSearchParams): SelectedMatchSearch {
  const kind = search.get(selectedMatchKindKey);
  const matchId = search.get(selectedMatchIdKey);
  const hasSelection = kind !== null || matchId !== null;
  if (!hasSelection) return { invalid: false, match: null };
  if (
    search.getAll(selectedMatchKindKey).length !== 1 ||
    search.getAll(selectedMatchIdKey).length !== 1 ||
    !matchId ||
    !uuidPattern.test(matchId) ||
    (kind !== "bracket_match" && kind !== "rubber")
  ) {
    return { invalid: true, match: null };
  }
  return { invalid: false, match: { kind, match_id: matchId } };
}

function TournamentRecoveryGuidance({
  recovery,
}: {
  recovery: Recovery | null;
}) {
  if (!recovery) return null;
  const unresolvedConstraints = recovery.schedule.unresolved_constraints ?? [];
  return (
    <Paper
      component="section"
      aria-label="Recovery guidance"
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Stack spacing={1}>
        <Typography component="h2" variant="h6">
          Recovery guidance
        </Typography>
        <Typography variant="body2">
          {`${recovery.execution.ready} ready · ${recovery.execution.blocked} blocked · ${recovery.execution.called} called · ${recovery.execution.active} active · ${recovery.execution.completed} completed`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {`${recovery.schedule.court_utilization_percent}% planned court utilization · ${recovery.schedule.planned_idle_minutes} planned idle minutes`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {`${recovery.schedule.delay_buckets.on_time} on time · ${recovery.schedule.delay_buckets.under_15_minutes} under 15 min late · ${recovery.schedule.delay_buckets.fifteen_to_thirty_minutes} 15–30 min late · ${recovery.schedule.delay_buckets.over_30_minutes} over 30 min late`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {recovery.schedule.average_participant_rest_minutes === null ||
          recovery.schedule.average_participant_rest_minutes === undefined
            ? "No observed participant rest interval yet."
            : `${recovery.schedule.average_participant_rest_minutes} average participant rest minutes`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {recovery.schedule.average_call_to_start_minutes === null ||
          recovery.schedule.average_call_to_start_minutes === undefined
            ? "No called match has started yet."
            : `${recovery.schedule.average_call_to_start_minutes} average call-to-start minutes`}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {`Dispatcher: ${label(recovery.dispatcher.mode)} · ${label(recovery.dispatcher.lease_state)} lease · ${label(recovery.dispatcher.lag_state)}`}
        </Typography>
        {unresolvedConstraints.map((constraint) => (
          <Typography
            key={constraint.code}
            color="text.secondary"
            variant="body2"
          >
            {`${constraint.count} ${label(constraint.code)} constraints`}
          </Typography>
        ))}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {recovery.actions.map((action) =>
            action.target === "order_of_play" ? (
              <Link
                component={RouterLink}
                key={action.code}
                to="?view=order"
                variant="body2"
              >
                {recoveryActionLabel[action.code]}
              </Link>
            ) : (
              <Link href="#dispatcher" key={action.code} variant="body2">
                {recoveryActionLabel[action.code]}
              </Link>
            ),
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function TournamentControlCenter({
  snapshot,
  presence,
  recovery,
  onCommand,
  onBulkPresence,
  onOpenScore,
  orderOfPlay,
}: {
  snapshot: Snapshot;
  presence: Presence[];
  recovery: Recovery | null;
  onCommand: (command: OperatorCommand) => Promise<void>;
  onBulkPresence: (batch: TournamentPresenceBatch) => Promise<void>;
  onOpenScore?: (match: MatchRow) => Promise<void>;
  orderOfPlay?: ReactNode;
}) {
  const compact = useMediaQuery("(max-width:600px)");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const submittedRef = useRef(new Set<string>());
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<(() => Promise<boolean>) | null>(null);
  const visible = useMemo(
    () =>
      presence.filter((row) =>
        (row.display_name ?? row.public_handle ?? "Participant")
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [presence, search],
  );

  const submit = async (body: OperatorCommand) => {
    const commandKey = [
      body.match.kind,
      body.match.match_id,
      body.command,
      body.expected_revision,
    ].join(":");
    if (pendingRef.current || submittedRef.current.has(commandKey))
      return false;
    pendingRef.current = true;
    submittedRef.current.add(commandKey);
    setPending(true);
    setError(null);
    const attempt = async () => {
      try {
        await onCommand(body);
        setRetry(null);
        return true;
      } catch (reason) {
        submittedRef.current.delete(commandKey);
        setError(
          reason instanceof Error ? reason.message : "Tournament action failed",
        );
        setRetry(() => () => submit(body));
        return false;
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    };
    return attempt();
  };

  useEffect(() => {
    submittedRef.current.clear();
  }, [snapshot.revision]);

  const submitBulk = async () => {
    const rows = presence.filter(
      (row) =>
        selected.has(row.participant_id) &&
        (row.allowed_actions ?? []).includes("operator_check_in"),
    );
    if (!rows.length || pending) return;
    setPending(true);
    try {
      await onBulkPresence({
        idempotency_key: crypto.randomUUID(),
        items: rows.slice(0, 100).map((row) => ({
          participant_id: row.participant_id,
          command: {
            command: "operator_check_in",
            idempotency_key: crypto.randomUUID(),
            expected_revision: row.revision,
          },
        })),
      });
      setSelected(new Set());
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Presence update failed",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography component="h1" variant="h5">
        Control Center
      </Typography>
      <Typography role="status" variant="body2" color="text.secondary">
        {pending
          ? "Saving tournament state…"
          : `Checked in ${snapshot.presence_summary.checked_in}`}
      </Typography>
      <TournamentRecoveryGuidance recovery={recovery} />
      {orderOfPlay}
      {error ? (
        <Alert
          severity="error"
          role="alert"
          action={
            retry ? (
              <Button size="small" onClick={() => void retry()}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      ) : null}

      <Paper
        component="section"
        aria-label="Participant presence"
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Search participants"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{ htmlInput: { "aria-label": "Participants" } }}
          />
          <Box
            component={compact ? "ul" : "div"}
            aria-label="Participant presence"
            role={compact ? "list" : undefined}
            sx={{ listStyle: "none", m: 0, p: 0 }}
          >
            {visible.map((row) => (
              <Stack
                component={compact ? "li" : "div"}
                key={row.participant_id}
                direction="row"
                sx={{
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                  py: 0.75,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={
                        !(row.allowed_actions ?? []).includes(
                          "operator_check_in",
                        )
                      }
                      checked={selected.has(row.participant_id)}
                      onChange={(_, checked) =>
                        setSelected((current) => {
                          const next = new Set(current);
                          if (checked) next.add(row.participant_id);
                          else next.delete(row.participant_id);
                          return next;
                        })
                      }
                    />
                  }
                  label={row.display_name ?? row.public_handle ?? "Participant"}
                />
                <Typography sx={{ ml: "auto" }} variant="body2">
                  {label(row.state)}
                </Typography>
              </Stack>
            ))}
          </Box>
          <Button
            size="small"
            variant="contained"
            disabled={!selected.size || pending}
            onClick={() => void submitBulk()}
          >
            Check in selected
          </Button>
        </Stack>
      </Paper>

      <Paper
        component="section"
        aria-label="Match queues"
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack spacing={1.5}>
          {snapshot.courts.map((court) => (
            <Typography key={court.court_id}>{court.label}</Typography>
          ))}
          {snapshot.matches.map((row) => {
            const executionActions = row.execution.allowed_actions ?? [];
            const readinessActions = row.readiness.allowed_actions ?? [];
            return (
              <Box
                key={`${row.match.kind}:${row.match.match_id}`}
                sx={{ borderBottom: 1, borderColor: "divider", pb: 1.5 }}
              >
                <Typography variant="subtitle2">
                  {row.match_display_label}
                </Typography>
                <Typography variant="body2">
                  {label(row.execution.state)}
                </Typography>
                {row.readiness.blockers.map((blocker) => (
                  <Typography
                    color="text.secondary"
                    key={blocker.code}
                    variant="body2"
                  >
                    {label(blocker.code)}
                  </Typography>
                ))}
                {executionActions.includes("call") &&
                readinessActions.includes("call") ? (
                  <Button
                    size="small"
                    disabled={pending}
                    onClick={() =>
                      void submit({
                        match: row.match,
                        command: "call",
                        idempotency_key: crypto.randomUUID(),
                        expected_revision: row.execution.revision,
                      })
                    }
                  >
                    Call to court
                  </Button>
                ) : null}
                {executionActions.includes("arrive_on_court") ? (
                  <Button
                    size="small"
                    disabled={pending}
                    onClick={() =>
                      void submit({
                        match: row.match,
                        command: "arrive_on_court",
                        idempotency_key: crypto.randomUUID(),
                        expected_revision: row.execution.revision,
                      })
                    }
                  >
                    Players on court
                  </Button>
                ) : null}
                {executionActions.includes("start_warmup") ? (
                  <Button
                    size="small"
                    disabled={pending}
                    onClick={() =>
                      void submit({
                        match: row.match,
                        command: "start_warmup",
                        idempotency_key: crypto.randomUUID(),
                        expected_revision: row.execution.revision,
                      })
                    }
                  >
                    Start warmup
                  </Button>
                ) : null}
                {onOpenScore &&
                ["warmup", "in_progress", "interval", "suspended"].includes(
                  row.execution.state,
                ) ? (
                  <Button
                    size="small"
                    disabled={pending}
                    onClick={() => void onOpenScore(row)}
                  >
                    Open umpire score
                  </Button>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    </Stack>
  );
}

export function TournamentControlCenterPage() {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [presence, setPresence] = useState<Presence[]>([]);
  const [recovery, setRecovery] = useState<Recovery | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [dispatcher, setDispatcher] = useState<
    Schema["TournamentDispatcherState"] | null
  >(null);
  const [recommendations, setRecommendations] = useState<
    Schema["TournamentDispatchDecision"][]
  >([]);
  const [scoreMatch, setScoreMatch] = useState<MatchRow | null>(null);
  const [score, setScore] = useState<Schema["TournamentScoreState"] | null>(
    null,
  );
  const [scoreLoadError, setScoreLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const refreshEpoch = useRef(0);
  const routeId = useRef(id);
  const liveStreamRevision = useRef(0);
  const boardHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const orderSearchRef = useRef<HTMLInputElement | null>(null);
  const viewFocusTarget = useRef<"board" | "order" | null>(null);
  const hasLiveStream = snapshot?.tournament_id === id;
  if (hasLiveStream) liveStreamRevision.current = snapshot.revision;
  routeId.current = id;
  const selectedMatchSearch = useMemo(
    () => selectedMatchFromSearch(searchParams),
    [searchParams],
  );
  const selectedMatch = selectedMatchSearch.match;
  const operatorView = searchParams.get("view") === "board" ? "board" : "order";
  const selectOperatorView = useCallback(
    (nextView: "board" | "order") => {
      viewFocusTarget.current = nextView;
      const next = new URLSearchParams(searchParams);
      if (nextView === "board") next.set("view", "board");
      else next.delete("view");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  useEffect(() => {
    if (viewFocusTarget.current !== operatorView) return;
    const target =
      operatorView === "board"
        ? boardHeadingRef.current
        : orderSearchRef.current;
    target?.focus();
    viewFocusTarget.current = null;
  }, [operatorView]);
  const selectMatch = useCallback(
    (match: MatchRef) => {
      const next = new URLSearchParams(searchParams);
      next.set(selectedMatchKindKey, match.kind);
      next.set(selectedMatchIdKey, match.match_id);
      setSelectionNotice(null);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const clearSelectedMatch = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(selectedMatchKindKey);
    next.delete(selectedMatchIdKey);
    setSelectionNotice(
      "Match details are unavailable. Select a current match.",
    );
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const closeSelectedMatch = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(selectedMatchKindKey);
    next.delete(selectedMatchIdKey);
    setSelectionNotice(null);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const refresh = useCallback(async () => {
    const tournamentId = id;
    const epoch = ++refreshEpoch.current;
    const [live, nextRecovery] = await Promise.all([
      tournamentDayApi.getTournamentOperatorLive(tournamentId),
      tournamentDayApi
        .getTournamentOperatorRecovery(tournamentId)
        .catch((reason: unknown) => {
          if ((reason as { status?: number }).status === 403) return null;
          throw reason;
        }),
    ]);
    if (epoch !== refreshEpoch.current || routeId.current !== tournamentId)
      return;
    setSnapshot(live);
    setRecovery(nextRecovery);
    setScoreMatch((current) =>
      current
        ? (live.matches.find(
            (row) =>
              row.match.kind === current.match.kind &&
              row.match.match_id === current.match.match_id,
          ) ?? null)
        : null,
    );
    try {
      const page = await tournamentDayApi.getTournamentPresence(tournamentId, {
        limit: 100,
      });
      if (epoch !== refreshEpoch.current || routeId.current !== tournamentId)
        return;
      setPresence(page.items);
      setNextCursor(page.next_cursor ?? null);
    } catch (reason) {
      if ((reason as { status?: number }).status !== 403) throw reason;
      setPresence([]);
      setNextCursor(null);
    }
    try {
      const [nextDispatcher, nextRecommendations] = await Promise.all([
        tournamentScheduleApi.getTournamentDispatcherState(tournamentId),
        tournamentScheduleApi.getTournamentDispatcherRecommendations(
          tournamentId,
        ),
      ]);
      if (epoch !== refreshEpoch.current || routeId.current !== tournamentId)
        return;
      setDispatcher(nextDispatcher);
      setRecommendations(nextRecommendations);
    } catch (reason) {
      if ((reason as { status?: number }).status !== 403) throw reason;
      setDispatcher(null);
      setRecommendations([]);
    }
    setError(null);
  }, [id]);
  useEffect(() => {
    refreshEpoch.current += 1;
    setSnapshot(null);
    setPresence([]);
    setRecovery(null);
    setNextCursor(null);
    setDispatcher(null);
    setRecommendations([]);
    setScoreMatch(null);
    setScore(null);
    setScoreLoadError(null);
    setError(null);
    setSelectionNotice(null);
    let active = true;
    void refresh().catch(
      (reason) =>
        active &&
        setError(
          reason instanceof Error
            ? reason.message
            : "Control center unavailable",
        ),
    );
    return () => {
      active = false;
    };
  }, [refresh]);
  useEffect(() => {
    if (!selectedMatchSearch.invalid) return;
    clearSelectedMatch();
  }, [clearSelectedMatch, selectedMatchSearch.invalid]);
  useEffect(() => {
    if (!hasLiveStream) return;
    let refreshInFlight = false;
    let refreshQueued = false;
    let terminal = false;
    const handleRefreshFailure = (reason: unknown) => {
      const status =
        reason && typeof reason === "object"
          ? (reason as { status?: unknown }).status
          : undefined;
      if ([401, 403, 404].includes(Number(status))) {
        setSnapshot(null);
        setPresence([]);
        setRecovery(null);
        setNextCursor(null);
        setDispatcher(null);
        setRecommendations([]);
        setError("Tournament access is no longer available.");
        terminal = true;
        return "terminal" as const;
      }
      setError("Live updates are temporarily unavailable.");
      return "rearm" as const;
    };
    const refreshAfterWake = () => {
      if (refreshInFlight) {
        refreshQueued = true;
        return;
      }
      refreshInFlight = true;
      void refresh()
        .catch(handleRefreshFailure)
        .finally(() => {
          refreshInFlight = false;
          if (refreshQueued && !terminal) {
            refreshQueued = false;
            refreshAfterWake();
          }
        });
    };
    const onStreamError = async () => {
      if (refreshInFlight) {
        refreshQueued = true;
        return "rearm" as const;
      }
      refreshInFlight = true;
      try {
        await refresh();
        return "rearm" as const;
      } catch (reason) {
        return handleRefreshFailure(reason);
      } finally {
        refreshInFlight = false;
        if (refreshQueued && !terminal) {
          refreshQueued = false;
          refreshAfterWake();
        }
      }
    };
    return connectBrowserTournamentLive({
      audience: "operator",
      credentials: "include",
      onRevision: (nextRevision, reset) => {
        if (reset || nextRevision > liveStreamRevision.current) {
          refreshAfterWake();
        }
      },
      onStreamError,
      revision: liveStreamRevision.current,
      tournamentId: id,
    });
  }, [hasLiveStream, id, refresh]);
  if (error)
    return (
      <Stack spacing={2}>
        <Typography component="h1" variant="h5">
          Control Center
        </Typography>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void refresh()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Stack>
    );
  if (!snapshot || snapshot.tournament_id !== id)
    return (
      <Stack spacing={2}>
        <Typography component="h1" variant="h5">
          Control Center
        </Typography>
        <Typography role="status">Loading control center…</Typography>
      </Stack>
    );
  const overrides = snapshot.matches
    .map((row) => ({
      match: row.match,
      expected_revision: row.execution.revision,
      allowed_actions: (row.execution.allowed_actions ?? []).filter((action) =>
        ["hold", "delay", "release", "re_court"].includes(action),
      ),
      label: row.match_display_label,
    }))
    .filter((row) => row.allowed_actions.length > 0);
  const loadScore = async (match: MatchRow) => {
    setScoreMatch(match);
    setScore(null);
    setScoreLoadError(null);
    try {
      setScore(
        await tournamentDayApi.getTournamentScoreState(
          match.match.kind,
          match.match.match_id,
        ),
      );
    } catch (reason) {
      setScoreLoadError(
        reason instanceof Error
          ? reason.message
          : "Score panel is unavailable.",
      );
    }
  };
  const openResolvedScore = async (
    resolution: Schema["TournamentScoreSheetResolution"],
  ) => {
    if (resolution.tournament_id !== id) {
      throw new Error("The game sheet belongs to a different tournament.");
    }
    const inspector =
      await tournamentDayApi.getTournamentOperatorMatchInspector(
        id,
        resolution.match.kind,
        resolution.match.match_id,
      );
    if (
      inspector.tournament_id !== id ||
      inspector.schedule_plan_revision !== resolution.schedule_plan_revision
    ) {
      throw new Error(
        "The game sheet target is no longer in the current schedule.",
      );
    }
    await loadScore({
      execution: inspector.execution,
      match: inspector.match,
      match_display_label: inspector.match_display_label,
      readiness: inspector.readiness,
    });
  };
  const gameSheetActions = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      {snapshot.can_generate_game_sheets ? (
        <GameSheetPackageDialog
          tournamentId={id}
          revision={snapshot.revision}
        />
      ) : null}
      <ScoreSheetResolverDialog onOpenScorePanel={openResolvedScore} />
    </Stack>
  );
  return (
    <Stack spacing={1}>
      <TournamentControlCenter
        orderOfPlay={
          <Stack spacing={1.5}>
            {selectionNotice ? (
              <Alert severity="info">{selectionNotice}</Alert>
            ) : null}
            {operatorView === "board" ? (
              <Stack direction="row" spacing={1}>
                <Button
                  aria-pressed={false}
                  onClick={() => selectOperatorView("order")}
                  size="small"
                  variant="outlined"
                >
                  Order of play
                </Button>
                <Button
                  aria-current="page"
                  disabled
                  size="small"
                  variant="contained"
                >
                  Court board
                </Button>
              </Stack>
            ) : null}
            <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {operatorView === "board" ? (
                  <OperatorOperationsBoard
                    focusHeadingRef={boardHeadingRef}
                    onSelectMatch={selectMatch}
                    revision={snapshot.revision}
                    selectedMatch={selectedMatch}
                    tournamentId={id}
                  />
                ) : (
                  <OperatorOrderOfPlay
                    actions={gameSheetActions}
                    focusSearchRef={orderSearchRef}
                    onOpenBoard={() => selectOperatorView("board")}
                    onSelectMatch={selectMatch}
                    revision={snapshot.revision}
                    selectedMatch={selectedMatch}
                    tournamentId={id}
                  />
                )}
              </Box>
              {selectedMatch ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <OperatorMatchInspector
                    match={selectedMatch}
                    onClose={closeSelectedMatch}
                    onUnavailable={clearSelectedMatch}
                    revision={snapshot.revision}
                    tournamentId={id}
                  />
                </Box>
              ) : null}
            </Stack>
            {operatorView === "board" ? gameSheetActions : null}
          </Stack>
        }
        snapshot={snapshot}
        presence={presence}
        recovery={recovery}
        onCommand={async (body) => {
          const { match, ...command } = body;
          await tournamentDayApi.applyTournamentExecutionCommand(
            match.kind,
            match.match_id,
            command,
          );
          await refresh();
        }}
        onBulkPresence={async (batch) => {
          await tournamentDayApi.applyTournamentPresenceBatch(id, batch);
          await refresh();
        }}
        onOpenScore={loadScore}
      />
      {scoreMatch ? (
        <UmpireScorePanel
          match={scoreMatch}
          score={score}
          loadError={scoreLoadError}
          onRetryLoad={async () => {
            await loadScore(scoreMatch);
          }}
          onScoreCommand={async (command) => {
            const next = await tournamentDayApi.applyTournamentScoreCommand(
              scoreMatch.match.kind,
              scoreMatch.match.match_id,
              command,
            );
            setScore(next);
            setScoreLoadError(null);
            return next;
          }}
          onExecutionCommand={async (command) => {
            await tournamentDayApi.applyTournamentExecutionCommand(
              scoreMatch.match.kind,
              scoreMatch.match.match_id,
              command,
            );
            await refresh();
          }}
          onFinalize={async (result) => {
            await tournamentDayApi.applyTournamentExecutionCommand(
              scoreMatch.match.kind,
              scoreMatch.match.match_id,
              {
                command: "finalize_played",
                idempotency_key: crypto.randomUUID(),
                expected_revision: scoreMatch.execution.revision,
                outcome: "played",
                winner_side: result.winner_side,
                final_result: result,
              },
            );
            setScoreMatch(null);
            setScore(null);
            setScoreLoadError(null);
            await refresh();
          }}
          onRefresh={async () => {
            const next = await tournamentDayApi.getTournamentScoreState(
              scoreMatch.match.kind,
              scoreMatch.match.match_id,
            );
            setScore(next);
            await refresh();
            return next;
          }}
        />
      ) : null}
      {nextCursor ? (
        <Button
          size="small"
          onClick={async () => {
            const tournamentId = id;
            const page = await tournamentDayApi.getTournamentPresence(
              tournamentId,
              { limit: 100, cursor: nextCursor },
            );
            if (routeId.current !== tournamentId) return;
            setPresence((current) => [...current, ...page.items]);
            setNextCursor(page.next_cursor ?? null);
          }}
        >
          Load more participants
        </Button>
      ) : null}
      {dispatcher ? (
        <Box id="dispatcher">
          <TournamentDispatchPanel
            dispatcher={dispatcher}
            recommendations={recommendations}
            overrides={overrides}
            courts={snapshot.courts}
            onRefresh={refresh}
            onControlCommand={async (command) => {
              await tournamentScheduleApi.applyTournamentDispatcherControlCommand(
                id,
                command,
              );
              void refresh().catch((reason) =>
                setError(
                  reason instanceof Error
                    ? reason.message
                    : "Dispatcher state needs refresh.",
                ),
              );
            }}
            onMatchOverride={async (match, command) => {
              await tournamentDayApi.applyTournamentDispatchCommand(
                match.kind,
                match.match_id,
                command,
              );
              void refresh().catch((reason) =>
                setError(
                  reason instanceof Error
                    ? reason.message
                    : "Tournament state needs refresh.",
                ),
              );
            }}
          />
        </Box>
      ) : null}
    </Stack>
  );
}
